import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import {
  useUpdateCompanyMutation,
  useCurrentSessionQuery,
  CompanyCategory,
  CompanySize,
  Language,
  CompanyTranslationInput,
  useCreateScorecardMutation,
} from "../../graphql/generated/graphql";
import { UploadedFile } from "../../types";
import UploadFile from "../../components/UploadFile/UploadFile";
import NavBar from "../../components/NavBar/NavBar";
import UpdateCompanySkeleton from "../../components/Skeletons/UpdateCompanySkeleton";
import DefaultImage from "../../image/upload.png";
import LoadingButton from "../../components/Button/LoadingButton";
import { defaultForms } from "../../components/ScoreCardData/DefaultForms";

interface FormValues {
  translations: CompanyTranslationInput[];
  category: CompanyCategory;
  companySize: CompanySize;
  languages: Language[];
  logo: string | null;
  business: string;
}

export default function UpdateCompany() {
  const { data: sessionData, loading: sessionLoading } =
    useCurrentSessionQuery();
  const [updateCompanyMutation, { loading: mutationLoading }] =
    useUpdateCompanyMutation();
  const [createScorecardMutation] = useCreateScorecardMutation();
  const navigate = useNavigate();

  const company = sessionData?.currentCompany || null;

  const initialValues: FormValues = useMemo(() => {
    return {
      translations: company?.translations?.nodes || [],
      category: company?.category || CompanyCategory.Other,
      companySize: company?.companySize || CompanySize["11To_50"],
      languages: (company?.languages || []).filter(
        (lang): lang is Language => lang !== null
      ),
      logo: company?.logoUrl || DefaultImage,
      business: "",
    };
  }, [company]);

  const validationSchema = Yup.object({
    business: Yup.string().required("وارد کردن نام شرکت الزامیست."),
    logo: Yup.string().nullable().required("آپلود لوگو الزامیست."),
  });

  const handleCreateDefaultScorecards = async () => {
    try {
      const responses = await Promise.all(
        defaultForms.map((form) =>
          createScorecardMutation({
            variables: {
              input: {
                scorecard: {
                  translations: form.translations,
                  criterias: form.criterias,
                },
              },
            },
          })
        )
      );

      const successfulCreations = responses.filter(
        (response) => response.data?.createScorecard?.scorecard
      );

      if (successfulCreations.length > 0) {
        return "";
      } else {
        toast.error("خطا!");
      }
    } catch (error) {
      toast.error("خطایی رخ داده است! لطفا دوباره تلاش کنید.");
    }
  };

  const formik = useFormik<FormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, actions) => {
      if (!company?.id) {
        console.error("اطلاعات شرکت موجود نیست!");
        return;
      }

      try {
        const patch = {
          translations: [
            { name: values.business, lang: Language.Fa },
            { name: values.business, lang: Language.En },
          ],
          category: values.category,
          companySize: values.companySize,
          languages: values.languages,
          ...(values.logo ? { logoExternalId: values.logo } : {}),
        };

        const response = await updateCompanyMutation({
          variables: {
            input: {
              id: company.id,
              patch,
            },
          },
        });

        if (response.errors) {
          toast.error("خطا در ثبت اطلاعات!");
        } else {
          toast.success("ثبت شد");
          await handleCreateDefaultScorecards();
          navigate("/jobs");
        }
      } catch (error) {
        toast.error("خطا در ثبت اطلاعات!");
      } finally {
        actions.setSubmitting(false);
      }
    },
  });

  const handleLogoChange = (fileUrl: string) => {
    toast.success("لوگو ذخیره شد");
  };

  const handleLogoData = (logoData: UploadedFile | null) => {
    formik.setFieldValue("logo", logoData?.externalId || null);
  };

  if (sessionLoading) return <UpdateCompanySkeleton />;

  return (
    <div className="h-svh w-screen">
      <NavBar name="ویرایش اطلاعات شرکت" />
      <form
        onSubmit={formik.handleSubmit}
        className="h-screen max-md:w-screen max-w-xl mx-auto md:my-32"
      >
        <div className="flex flex-col gap-4 mx-6">
          <p className="text-md font-semibold">تصویر لوگو</p>
          <UploadFile
            setFile={handleLogoChange}
            setLogo={handleLogoData}
            defaultImage={DefaultImage}
          />
          {formik.errors.logo && (
            <div className="text-red-500 text-sm">{formik.errors.logo}</div>
          )}
          <div className="flex flex-col gap-2">
            <label htmlFor="business" className="font-medium">
              نام تجاری
            </label>
            <input
              type="text"
              name="business"
              value={formik.values.business}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="shadow-sm border-2 border-gray-300 rounded-lg p-2 outline-none"
            />
            {formik.errors.business && formik.touched.business && (
              <div className="text-red-500 text-sm">
                {formik.errors.business}
              </div>
            )}
          </div>
          <div className="my-6">
            <button
              type="submit"
              disabled={mutationLoading}
              className={`w-full py-2 px-4 rounded-md text-white ${
                mutationLoading
                  ? "bg-gray-500 cursor-not-allowed"
                  : "bg-primary hover:bg-opacity-85"
              }`}
            >
              {mutationLoading ? <LoadingButton /> : "ذخیره تغییرات"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
